import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
} from "@chakra-ui/react";
import { BiX } from "react-icons/bi";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import CustomSelect from "../../CustomSelect";

const defaultStatusOptions = [
  "Not Pick",
  "Follow up",
  "Call Occupied",
  "Customer Occupied",
  "Not Connected",
  "Not Interested",
  "Switch Off",
  "Interested",
];

const PeoplesDrawer = ({ closeDrawerHandler, fetchAllPeople }) => {
  const [cookies] = useCookies();
  const [companies, setCompanies] = useState([]);
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  // const [companyId, setCompanyId] = useState('');
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("");
  const [statusOptions, setStatusOptions] = useState([]);
  const [customStatus, setCustomStatus] = useState("");
  const [comment, setComment] = useState("");
  const [products, setProducts] = useState([]);
  const [defaultProductId, setDefaultProductId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const dispatch = useDispatch();
  const auth = useSelector(state => state.auth);

  const addPeopleHandler = async (e) => {
    e.preventDefault();

    if (isSubmittingRef.current) return;

    if (firstname.length > 25) {
      toast.error("Firstname field must be less than 25 characters long");
      return;
    }
    if (lastname.length > 25) {
      toast.error("Lastname field must be less than 25 characters long");
      return;
    }
    if (phone.length > 10) {
      toast.error("Phone no. field must be 10 digits long");
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      const baseURL = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(baseURL + "people/create-people", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${cookies?.access_token}`,
        },
        body: JSON.stringify({
          firstname: firstname,
          lastname: lastname,
          email: email,
          // company: companyId,
          phone: phone,
          ...(status ? { status } : {}),
          ...(comment ? { comment } : {}),
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message);
      }

      if (status === "Interested") {
        await autoCreateLead(data?.person?._id);
      }

      fetchAllPeople();
      closeDrawerHandler();
      toast.success(data.message);
    } catch (err) {
      toast.error(err.message);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const getAllCompanies = async () => {
    try {
      const baseURL = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(baseURL + "company/all-companies", {
        method: "POST",
        headers: {
          authorization: `Bearer ${cookies?.access_token}`,
        },
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message);
      }

      setCompanies(data.companies);
    } catch (err) {
      toast(err.message);
    }
  };

  const getAllStatuses = async () => {
    try {
      const baseURL = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(baseURL + "status/all-statuses", {
        method: "POST",
        headers: {
          authorization: `Bearer ${cookies?.access_token}`,
        },
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message);
      }

      const fetched = data.statuses.map((status) => status.name);
      const merged = Array.from(new Set([...defaultStatusOptions, ...fetched]));
      setStatusOptions(merged);
    } catch (err) {
      // If no statuses exist or API fails, use default ones
      setStatusOptions(defaultStatusOptions);
    }
  };

  const addStatus = async (statusName) => {
    try {
      const baseURL = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(baseURL + "status/create-status", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${cookies?.access_token}`,
        },
        body: JSON.stringify({
          name: statusName,
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message);
      }

      return data;
    } catch (err) {
      throw err;
    }
  };

  const getAllProducts = async () => {
    try {
      const baseURL = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(baseURL + "product/all-products", {
        method: "POST",
        headers: {
          authorization: `Bearer ${cookies?.access_token}`,
        },
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message);
      }

      setProducts(data.products || []);
      if (!defaultProductId && data.products?.length) {
        setDefaultProductId(data.products[0]?._id || "");
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const autoCreateLead = async (peopleId) => {
    if (!peopleId) return;

    if (!defaultProductId) {
      toast.error("Unable to create lead automatically. Please add a product first.");
      return;
    }

    try {
      const baseURL = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(baseURL + "lead/create-lead", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${cookies?.access_token}`,
        },
        body: JSON.stringify({
          leadtype: "People",
          status: "New",
          source: "Sales",
          peopleId,
          products: [defaultProductId],
        }),
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.message);
      }

      toast.success("Lead created automatically");
    } catch (err) {
      toast.error(`Lead creation failed: ${err.message}`);
    }
  };

  useEffect(() => {
    getAllCompanies();
    getAllStatuses();
    getAllProducts();
  }, []);

  return (
    <div
      className="absolute overflow-auto h-[100vh] w-[90vw] md:w-[450px] bg-white right-0 top-0 z-10 py-3"
      style={{
        boxShadow:
          "rgba(0, 0, 0, 0.08) 0px 6px 16px 0px, rgba(0, 0, 0, 0.12) 0px 3px 6px -4px, rgba(0, 0, 0, 0.05) 0px 9px 28px 8px",
      }}
    >
      <h1 className="px-4 flex gap-x-2 items-center text-xl py-3 border-b">
        <BiX onClick={closeDrawerHandler} size="26px" />
        Individuals
      </h1>

      <div className="mt-8 px-5">
        <h2 className="text-2xl font-bold py-5 text-center mb-6 border-y bg-blue-200 rounded-lg shadow-md">
          Add New Individual
        </h2>

        <form onSubmit={addPeopleHandler} className="space-y-5">
          {/* First Name */}
          <FormControl className="mt-3 mb-5" isRequired>
            <FormLabel fontWeight="bold" className="text-[#4B5563]">
              First Name
            </FormLabel>
            <Input
              value={firstname}
              onChange={(e) => setFirstname(e.target.value)}
              type="text"
              placeholder="Enter First Name"
              className="rounded mt-2 border p-3 focus:ring-2 focus:ring-blue-400"
            />
          </FormControl>
          <FormControl className="mt-3 mb-5" isRequired>
            <FormLabel fontWeight="bold" className="text-[#4B5563]">
              Last Name
            </FormLabel>
            <Input
              value={lastname}
              onChange={(e) => setLastname(e.target.value)}
              type="text"
              placeholder="Enter Last Name"
              className="rounded mt-2 border p-3 focus:ring-2 focus:ring-blue-400"
            />
          </FormControl>

          {/* Status with Add More */}
          <FormControl className="mt-3 mb-5">
            <FormLabel fontWeight="bold" className="text-[#4B5563]">
              Status
            </FormLabel>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              placeholder="Select status"
            >
              {statusOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </Select>
            {auth?.role === "Super Admin" && (
              <div className="flex gap-2 mt-2">
                <Input
                  value={customStatus}
                  onChange={(e) => setCustomStatus(e.target.value)}
                  placeholder="Add more status"
                  disabled={statusOptions.length >= 10}
                />
                <Button
                  onClick={async (e) => {
                    e.preventDefault();
                    const val = customStatus.trim();
                    if (!val) return;

                    if (statusOptions.length >= 10) {
                      toast.error("Maximum 10 status options allowed");
                      return;
                    }

                    if (!statusOptions.includes(val)) {
                      try {
                        await addStatus(val);
                        setStatusOptions((prev) => [...prev, val]);
                        setStatus(val);
                        toast.success("Status added successfully");
                      } catch (err) {
                        toast.error(err.message);
                      }
                    } else {
                      toast.error("Status already exists");
                    }
                    setCustomStatus("");
                  }}
                  disabled={statusOptions.length >= 10}
                >
                  Add
                </Button>
              </div>
            )}
          </FormControl>

          {/* Last Name */}

          {/* Corporate Selection (Optional) */}
          <FormControl className="mt-2 mb-5">
            {/* If you decide to enable corporate selection, you can uncomment and use the following */}
            {/* <FormLabel fontWeight="bold" className="text-[#4B5563]">Corporate</FormLabel>
      <Select value={companyId} onChange={(e) => setCompanyId(e.target.value)} placeholder="Select Corporate">
        {companies.map((company) => (
          <option key={company._id} value={company._id}>
            {company.companyname}
          </option>
        ))}
      </Select> */}
            {/* Or if using the custom select component */}
            {/* <CustomSelect
        data={companies}
        idAccessor={'_id'}
        nameAccessor={"companyname"}
        label={"Corporate"}
        placeholder={"Select corporate"}
        value={companyId}
        setValue={setCompanyId}
      /> */}
          </FormControl>

          {/* Phone */}
          <FormControl className="mt-3 mb-5" isRequired>
            <FormLabel fontWeight="bold" className="text-[#4B5563]">
              Phone
            </FormLabel>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              type="number"
              placeholder="Enter Phone Number"
              className="rounded mt-2 border p-3 focus:ring-2 focus:ring-blue-400"
              maxLength={10}
            />
          </FormControl>

          {/* Email */}
          <FormControl className="mt-3 mb-5" isRequired>
            <FormLabel fontWeight="bold" className="text-[#4B5563]">
              Email
            </FormLabel>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              placeholder="Enter Email Address"
              className="rounded mt-2 border p-3 focus:ring-2 focus:ring-blue-400"
            />
          </FormControl>

          {/* Comment */}
          <FormControl className="mt-3 mb-5">
            <FormLabel fontWeight="bold" className="text-[#4B5563]">
              Comment
            </FormLabel>
            <Input
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              type="text"
              placeholder="Enter any additional comments"
              className="rounded mt-2 border p-3 focus:ring-2 focus:ring-blue-400"
            />
          </FormControl>

          {/* Submit Button */}
          <Button
            type="submit"
            className="mt-1 w-full py-3 text-white font-bold rounded-lg hover:bg-blue-600 transition duration-300"
            colorScheme="blue"
            isLoading={isSubmitting}
            loadingText="Submitting..."
            disabled={isSubmitting}
          >
            Submit
          </Button>
        </form>
      </div>
    </div>
  );
};

export default PeoplesDrawer;
